import { defineStore } from 'pinia';
import { FAVORITES_FEATURE_MAX_ITEMS_LIMIT, Store, WindowsStorage } from '@/setup/globals';
import useNotificationStore from '@/stores/notification';
import useSessionStore from '@/stores/session';
import { downloadBlob } from '@/helpers/create-blob';
import getApiUrl from '@/helpers/get-api-url';
import { ApiFavoritesProductList } from '@/types/api-response';
import { FavoritesProduct } from '@/types/product';

export type StoreState = {
  ids: string[];
  favorites: FavoritesProduct[] | null;
  filterResultFavorites: FavoritesProduct[] | null;

  /**
   * Holds the currently running requests.
   */
  runningRequests: {
    apiFetchFavorites: boolean;
    apiExportList: boolean;
    apiExportTendertexts: boolean;
    apiExportDatasheets: boolean;
  };
};

const storeName = Store.Favorites;

function updateIdsInLocalStorage(favorites: FavoritesProduct[]): void {
  try {
    window.localStorage.setItem(WindowsStorage.FavoritesLastSaved, Date.now().toString());
    window.localStorage.setItem(WindowsStorage.FavoritesIds, JSON.stringify(favorites.map((favorite) => favorite.id)));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    console.error(new Error('An error occurred while setting favorites IDs in localStorage.')); // eslint-disable-line no-console
  }
}

/**
 * Gets localStorage favoritesIds if still valid.
 */
function getFavoritesIdsFromStorage(): string[] {
  const lastSaved = parseInt(window.localStorage.getItem(WindowsStorage.FavoritesLastSaved) ?? '', 10);
  const oneYearInMs = 365 * 24 * 60 * 1000;

  if (lastSaved && Date.now() - lastSaved > oneYearInMs) {
    return [];
  }

  try {
    const ids = window.localStorage.getItem(WindowsStorage.FavoritesIds);

    if (ids) {
      const parsedIds = JSON.parse(ids);

      return Array.isArray(parsedIds) ? parsedIds : [];
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    updateIdsInLocalStorage([]);
    // eslint-disable-next-line no-console
    console.error(
      new Error(
        'An error occurred while getting favorites from localStorage: Data Corrupt. Resetting Favorite Ids Entry',
      ),
    );
  }

  return [];
}

// Utility function to decode Base64 and create a Blob
function base64ToBlob(base64: string, mimeType: string): Blob {
  const binaryString = atob(base64); // Decode Base64 into binary string
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i); // Convert each character to byte
  }

  return new Blob([bytes], { type: mimeType }); // Create a Blob from the bytes
}

function formatDateForFile(date: Date): string {
  const pad = (num: number) => num.toString().padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1); // Months are 0-indexed
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${day}_${month}_${year}_${hours}_${minutes}`;
}

export default defineStore(storeName, {
  state: () => {
    const state: StoreState = {
      ids: getFavoritesIdsFromStorage(),
      favorites: null,
      filterResultFavorites: null,

      runningRequests: {
        apiFetchFavorites: false,
        apiExportList: false,
        apiExportTendertexts: false,
        apiExportDatasheets: false,
      },
    };

    return state;
  },
  actions: {
    async apiFetchFavorites(): Promise<ApiFavoritesProductList> {
      this.runningRequests.apiFetchFavorites = true;

      const sessionStore = useSessionStore();
      const url = getApiUrl('getFavorites', {
        locale: sessionStore.locale,
        ids: this.ids.join(','),
      });

      try {
        const response = await this.$api.get(url);

        if ('data' in response) {
          this.favorites = response.data.data;

          return response.data.data;
        }

        throw new Error('API failure during "getFavorites" request.');
      } catch (error) {
        return Promise.reject(error); // Explicitly reject with the caught e
      } finally {
        this.runningRequests.apiFetchFavorites = false;
      }
    },

    async apiFetchFavoritesByIds(ids: number[]): Promise<ApiFavoritesProductList> {
      this.runningRequests.apiFetchFavorites = true;

      const sessionStore = useSessionStore();
      const url = getApiUrl('getFavorites', {
        locale: sessionStore.locale,
        ids: ids.join(','),
      });

      try {
        const response = await this.$api.get(url);

        if ('data' in response) {
          this.filterResultFavorites = response.data.data;

          return response.data.data;
        }

        throw new Error('API failure during "getFavoritesByIds" request.');
      } catch (error) {
        return Promise.reject(error); // Explicitly reject with the caught e
      } finally {
        this.runningRequests.apiFetchFavorites = false;
      }
    },

    showNotification(errorMessage: string, messageType?: string): void {
      useNotificationStore().showNotification({
        type: messageType || 'error',
        message: errorMessage,
        expire: false,
      });
    },

    addFavorite(product: FavoritesProduct, limitReachedMessage: string): void {
      if (!this.favorites) {
        this.favorites = [];
      }

      if (this.favorites.length >= FAVORITES_FEATURE_MAX_ITEMS_LIMIT) {
        this.showNotification(limitReachedMessage);

        return;
      }

      if (!this.favorites.find((favorite) => favorite.id === product.id)) {
        this.favorites.push(product);
      }

      updateIdsInLocalStorage(this.favorites);
    },

    removeFavorite(product: FavoritesProduct): void {
      if (!this.favorites) {
        return;
      }

      this.favorites = this.favorites.filter((favorite) => favorite.id !== product.id);

      updateIdsInLocalStorage(this.favorites);
    },

    clearList(): void {
      if (!this.favorites) {
        return;
      }

      this.favorites = [];
      updateIdsInLocalStorage(this.favorites);
    },

    async exportList(filenamePrefix: string): Promise<void> {
      this.runningRequests.apiExportList = true;

      const url = getApiUrl('getExportList', {
        locale: useSessionStore().locale,
        ids: this.ids.join(','),
      });

      try {
        const response = await this.$api.get(url, {
          responseType: 'blob',
        });

        downloadBlob(
          response.data,
          'application/vnd.ms-excel',
          `${filenamePrefix}_${formatDateForFile(new Date())}`,
          'xlsx',
        );
      } finally {
        this.runningRequests.apiExportList = false;
      }
    },

    async exportTendertexts(filenamePrefix: string): Promise<void> {
      this.runningRequests.apiExportTendertexts = true;

      const url = getApiUrl('getExportTendertexts', {
        locale: useSessionStore().locale,
        ids: this.ids.join(','),
      });

      try {
        const response = await this.$api.get(url);

        if (response.data.data && response.data.data.fileContent) {
          // Decode the Base64-encoded file content
          const blob = base64ToBlob(response.data.data.fileContent, 'application/zip');

          // Trigger the download
          downloadBlob(blob, 'application/zip', `${filenamePrefix}_${formatDateForFile(new Date())}`, 'zip');
        }
      } finally {
        this.runningRequests.apiExportTendertexts = false;
      }
    },

    async exportDatasheets(filenamePrefix: string): Promise<void> {
      this.runningRequests.apiExportDatasheets = true;

      const url = getApiUrl('getExportDatasheets', {
        locale: useSessionStore().locale,
        ids: this.ids.join(','),
      });

      try {
        const response = await this.$api.get(url);

        if (response.data.data && response.data.data.fileContent) {
          // Decode the Base64-encoded file content
          const blob = base64ToBlob(response.data.data.fileContent, 'application/zip');

          // Trigger the download
          downloadBlob(blob, 'application/zip', `${filenamePrefix}_${formatDateForFile(new Date())}`, 'zip');
        }
      } finally {
        this.runningRequests.apiExportDatasheets = false;
      }
    },
  },
});
